import papaparse from 'papaparse';
import { Candidate } from '../types'; // Assuming Candidate type includes all necessary score fields

/**
 * Exports analysis results to a CSV file and triggers download.
 * @param results Array of Candidate objects containing analysis results.
 * @param jobTitle Optional title for the job, used in the filename.
 */
export function exportResultsToCsv(results: Candidate[], jobTitle: string = 'Analysis'): void {
  if (!results || results.length === 0) {
    console.warn('No results to export.');
    return;
  }

  // Define CSV headers
  const headers = [
    'Candidate Name',
    'File Name',
    'Overall Match Score',
    'Experience Score',
    'Skills Score',
    'Language Score',
    'Qualifications Score',
    // Add other relevant fields if needed, e.g., 'Analysis Summary'
    // 'Analysis Summary' 
  ];

  // Map results to CSV data rows
  const data = results.map(candidate => ({
    'Candidate Name': candidate.name || 'N/A',
    'File Name': candidate.fileName || 'N/A',
    'Overall Match Score': candidate.matchScore ?? 0, // Use nullish coalescing for default
    'Experience Score': candidate.experienceScore ?? 0,
    'Skills Score': candidate.skillsScore ?? 0,
    'Language Score': candidate.languageScore ?? 0,
    'Qualifications Score': candidate.qualificationsScore ?? 0,
    // 'Analysis Summary': candidate.analysis?.replace(/\n/g, ' ') || 'N/A' // Replace newlines for CSV
  }));

  // Convert data to CSV string using papaparse
  const csvString = papaparse.unparse({
    fields: headers,
    data: data.map(row => headers.map(header => row[header as keyof typeof row])) // Ensure order matches headers
  });

  // Create a Blob from the CSV string
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

  // Create a temporary link element
  const link = document.createElement('a');
  if (link.download !== undefined) { // Check if download attribute is supported
    // Create an object URL for the blob
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Sanitize job title for filename
    const sanitizedJobTitle = jobTitle.replace(/[^a-z0-9_-]/gi, '_');
    link.setAttribute('download', `${sanitizedJobTitle}_Results_${new Date().toISOString().split('T')[0]}.csv`);
    
    // Append link to body, click it, and remove it
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the object URL to free up memory
    URL.revokeObjectURL(url);
    console.log('CSV export triggered successfully.');
  } else {
    console.error('CSV download is not supported in this browser.');
    // Optionally, display the CSV data or provide alternative download method
  }
} 